import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../api/client";

export interface AgentCapability {
  id: string;
  namespace: string;
  description: string;
  taxonomy_level: number;
}

export interface AvailableAgent {
  id: string;
  name: string;
  surface: string;
  is_xamun_default: boolean;
  auth_method: string;
  description: string;
  capabilities: AgentCapability[];
  is_bound_in_workspace: boolean;
}

export interface AgentBinding {
  id: string;
  agent_definition_id: string;
  agent_name: string;
  surface: string;
  enabled: boolean;
  scope: Record<string, unknown>;
  bound_by_user: { id: string; email: string; display_name: string };
  bound_at: string;
  expires_at: string | null;
  last_health_check_at: string | null;
  last_invocation_at: string | null;
  telemetry: Record<string, unknown>;
}

export interface CoverageRow {
  id: string;
  namespace: string;
  description: string;
  taxonomy_level: number;
  covered_by: Array<{ binding_id: string; agent_name: string }>;
}

export interface CoverageMapResponse {
  capabilities: CoverageRow[];
  summary: { total: number; covered: number; gaps: number };
}

/**
 * Company-scoped LLM config as returned by /api/workspaces/:wid/llm-configs.
 * One row per provider; exactly one row has isDefault=true once the company
 * has any configured provider.
 */
export interface CompanyLlmConfig {
  provider: "anthropic" | "openai" | "gemini" | "azure_openai";
  config: { model?: string;[k: string]: unknown };
  routingRules: Record<string, unknown>;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useAvailableAgents(workspaceId: string) {
  return useQuery<{ agents: AvailableAgent[] }, Error>({
    queryKey: ["agents", workspaceId, "available"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ agents: AvailableAgent[] }>(
        `/api/workspaces/${workspaceId}/agents/available`,
      ),
  });
}

export function useBindings(workspaceId: string) {
  return useQuery<{ bindings: AgentBinding[] }, Error>({
    queryKey: ["agents", workspaceId, "bindings"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ bindings: AgentBinding[] }>(
        `/api/workspaces/${workspaceId}/agents/bindings`,
      ),
  });
}

export function useCoverageMap(workspaceId: string) {
  return useQuery<CoverageMapResponse, Error>({
    queryKey: ["agents", workspaceId, "coverage-map"],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<CoverageMapResponse>(
        `/api/workspaces/${workspaceId}/agents/coverage-map`,
      ),
  });
}

export interface BindAgentBody {
  agent_definition_name: string;
  scope?: Record<string, unknown>;
  credentials: Record<string, unknown>;
}

export interface BindAgentResponse {
  id?: string;
  agent_definition_id?: string;
  agent_name?: string;
  surface?: string;
  enabled?: boolean;
  scope?: Record<string, unknown>;
  bound_at?: string;
  // OAuth-redirect variant for copilot
  next_step?: "oauth_redirect";
  authorize_url?: string;
  state?: string;
}

export function useAgentMutations(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["agents", workspaceId] });
  };

  const bind = useMutation<BindAgentResponse, Error, BindAgentBody>({
    mutationFn: (body) =>
      apiPost<BindAgentResponse>(
        `/api/workspaces/${workspaceId}/agents/bindings`,
        body,
      ),
    onSuccess: invalidate,
  });

  const revoke = useMutation<unknown, Error, string>({
    mutationFn: (bindingId) =>
      apiDelete(
        `/api/workspaces/${workspaceId}/agents/bindings/${bindingId}`,
      ),
    onSuccess: invalidate,
  });

  const testConnection = useMutation<
    { ok: boolean; error?: string },
    Error,
    string
  >({
    mutationFn: (bindingId) =>
      apiPost<{ ok: boolean; error?: string }>(
        `/api/workspaces/${workspaceId}/agents/bindings/${bindingId}/test-connection`,
        {},
      ),
  });

  return { bind, revoke, testConnection };
}

/**
 * Lists every LLM config configured for the workspace's company. Returns
 * `{items: [...]}`; empty array = no key configured (the gating signal for
 * agent runs). The component-friendly `hasDefault` derives the inactive
 * state shown across HandsBuilder + AgentManagement.
 */
export function useCompanyLlmConfigs(workspaceId: string) {
  return useQuery<{ items: CompanyLlmConfig[] }, Error>({
    queryKey: ["llm-configs", workspaceId],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ items: CompanyLlmConfig[] }>(
        `/api/workspaces/${workspaceId}/llm-configs`,
      ),
  });
}

/** Convenience: did this workspace's company configure any LLM key? */
export function useHasLlmKey(workspaceId: string): {
  isLoading: boolean;
  hasDefault: boolean;
  defaultProvider: CompanyLlmConfig["provider"] | null;
} {
  const q = useCompanyLlmConfigs(workspaceId);
  const items = q.data?.items ?? [];
  const def = items.find((c) => c.isDefault) ?? null;
  return {
    isLoading: q.isLoading,
    hasDefault: !!def,
    defaultProvider: def?.provider ?? null,
  };
}

export interface SetCompanyLlmConfigBody {
  credentials: Record<string, unknown>;
  config?: Record<string, unknown>;
  routing_rules?: Record<string, unknown>;
  is_default?: boolean;
}

export function useCompanyLlmMutations(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["llm-configs", workspaceId] });

  const set = useMutation<
    unknown,
    Error,
    { provider: CompanyLlmConfig["provider"]; body: SetCompanyLlmConfigBody }
  >({
    mutationFn: ({ provider, body }) =>
      apiPut(
        `/api/workspaces/${workspaceId}/llm-configs/${provider}`,
        body,
      ),
    onSuccess: invalidate,
  });

  const promoteDefault = useMutation<
    unknown,
    Error,
    CompanyLlmConfig["provider"]
  >({
    mutationFn: (provider) =>
      apiPost(
        `/api/workspaces/${workspaceId}/llm-configs/${provider}/default`,
        {},
      ),
    onSuccess: invalidate,
  });

  const test = useMutation<
    { ok: boolean; detail?: string },
    Error,
    CompanyLlmConfig["provider"]
  >({
    mutationFn: (provider) =>
      apiPost<{ ok: boolean; detail?: string }>(
        `/api/workspaces/${workspaceId}/llm-configs/${provider}/test`,
        {},
      ),
  });

  const remove = useMutation<unknown, Error, CompanyLlmConfig["provider"]>({
    mutationFn: (provider) =>
      apiDelete(`/api/workspaces/${workspaceId}/llm-configs/${provider}`),
    onSuccess: invalidate,
  });

  return { set, promoteDefault, test, remove };
}

// ─── Voice (ElevenLabs) BYOK ───────────────────────────────────────────────

/**
 * Company-scoped voice config as returned by
 * /api/workspaces/:wid/voice-configs. One row per provider (only ElevenLabs
 * today). `config.sip`/`config.whatsapp` are scaffolded for the telephony PR
 * and inert until then.
 */
export interface CompanyVoiceConfig {
  provider: "elevenlabs";
  config: {
    default_voice_id?: string;
    default_model_id?: string;
    sip?: Record<string, unknown>;
    whatsapp?: Record<string, unknown>;
    [k: string]: unknown;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  category?: string;
}

export function useCompanyVoiceConfigs(workspaceId: string) {
  return useQuery<{ items: CompanyVoiceConfig[] }, Error>({
    queryKey: ["voice-configs", workspaceId],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ items: CompanyVoiceConfig[] }>(
        `/api/workspaces/${workspaceId}/voice-configs`,
      ),
  });
}

export interface SetCompanyVoiceConfigBody {
  credentials: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export interface ConfigureSipBody {
  phone_number: string;
  label: string;
  address: string;
  transport?: "auto" | "udp" | "tcp" | "tls";
  media_encryption?: "disabled" | "allowed" | "required";
  username?: string;
  password?: string;
}

export interface ElevenLabsAgentSummary {
  agent_id: string;
  name: string;
}

export interface ElevenLabsVoiceOption {
  voice_id: string;
  name: string;
  category?: string;
}

/** Voices available on the tenant's ElevenLabs account (voice picker). */
export function useVoices(workspaceId: string, enabled: boolean) {
  return useQuery<{ voices: ElevenLabsVoiceOption[] }, Error>({
    queryKey: ["voice-voices", workspaceId],
    enabled: !!workspaceId && enabled,
    queryFn: () =>
      apiGet<{ voices: ElevenLabsVoiceOption[] }>(
        `/api/workspaces/${workspaceId}/voice-configs/elevenlabs/voices`,
      ),
  });
}

/**
 * Fetch a short TTS preview clip as a playable object URL. Returns the raw
 * audio (the /tts endpoint streams bytes, not JSON), so this bypasses the JSON
 * api client. Caller is responsible for URL.revokeObjectURL.
 */
export async function fetchVoicePreviewUrl(
  workspaceId: string,
  body: { text: string; voice_id?: string; model_id?: string },
): Promise<string> {
  const res = await fetch(
    `/api/workspaces/${workspaceId}/voice-configs/elevenlabs/tts`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) detail = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Referenceable agents from the tenant's ElevenLabs account (agent picker). */
export function useReferenceableAgents(workspaceId: string, enabled: boolean) {
  return useQuery<{ agents: ElevenLabsAgentSummary[] }, Error>({
    queryKey: ["voice-agents", workspaceId],
    enabled: !!workspaceId && enabled,
    queryFn: () =>
      apiGet<{ agents: ElevenLabsAgentSummary[] }>(
        `/api/workspaces/${workspaceId}/voice-configs/elevenlabs/agents`,
      ),
  });
}

export function useCompanyVoiceMutations(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["voice-configs", workspaceId] });

  const set = useMutation<
    unknown,
    Error,
    { provider: CompanyVoiceConfig["provider"]; body: SetCompanyVoiceConfigBody }
  >({
    mutationFn: ({ provider, body }) =>
      apiPut(
        `/api/workspaces/${workspaceId}/voice-configs/${provider}`,
        body,
      ),
    onSuccess: invalidate,
  });

  const test = useMutation<
    { ok: boolean; detail?: string },
    Error,
    CompanyVoiceConfig["provider"]
  >({
    mutationFn: (provider) =>
      apiPost<{ ok: boolean; detail?: string }>(
        `/api/workspaces/${workspaceId}/voice-configs/${provider}/test`,
        {},
      ),
  });

  const remove = useMutation<unknown, Error, CompanyVoiceConfig["provider"]>({
    mutationFn: (provider) =>
      apiDelete(`/api/workspaces/${workspaceId}/voice-configs/${provider}`),
    onSuccess: invalidate,
  });

  const configureSip = useMutation<unknown, Error, ConfigureSipBody>({
    mutationFn: (body) =>
      apiPut(`/api/workspaces/${workspaceId}/voice-configs/elevenlabs/sip`, body),
    onSuccess: invalidate,
  });

  const setAgent = useMutation<
    unknown,
    Error,
    {
      default_agent_id?: string;
      default_voice_id?: string;
      default_model_id?: string;
      whatsapp?: { phone_number_id?: string; agent_id?: string };
    }
  >({
    mutationFn: (body) =>
      apiPut(`/api/workspaces/${workspaceId}/voice-configs/elevenlabs/agent`, body),
    onSuccess: invalidate,
  });

  const outboundCall = useMutation<
    { ok: boolean; result?: unknown },
    Error,
    { to_number: string; agent_id?: string; phone_number_id?: string }
  >({
    mutationFn: (body) =>
      apiPost<{ ok: boolean; result?: unknown }>(
        `/api/workspaces/${workspaceId}/voice-configs/elevenlabs/outbound-call`,
        body,
      ),
  });

  return { set, test, remove, configureSip, setAgent, outboundCall };
}
